import cx from 'classnames';
import Image from 'next/image';

import { useTrackEventAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../../design-system/components/form/button/Button';
import { BrandHandMarkTwoLinesVariant2 } from '../../../../../design-system/components/illustration/BrandHandMarkTwoLinesVariant2';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import { ComboImageAndTextBlockSliceType } from '../../../../../domains/Prismic/BrandPages/BrandPagesDocument';
import { EvaneosPrismicRichText } from '../../../../../domains/Prismic/EvaneosPrismicRichText';

import style from './ComboImageAndTextBlockSlice.module.css';

export type ComboImageAndTextBlockSliceProps = ComboImageAndTextBlockSliceType;

export function ComboImageAndTextBlockSlice({
    subtitle,
    title,
    version,
    text,
    picture,
    imagePosition,
    ctaLabel,
    ctaLink,
    secondCtaLabel,
    secondCtaLink,
    target,
    secondCtaTarget,
    sliceIndex,
}: ComboImageAndTextBlockSliceProps & { sliceIndex?: number }) {
    const { trackEvent } = useTrackEventAmplitude();
    const isLightBackground = version === 'yellow' || version === 'white';
    const isButtonPrimary = version === 'green' || version === 'white';
    const hasTwoButtons = secondCtaLabel && secondCtaLink?.url;

    return (
        <div
            className={cx(style.wrapper, {
                [style.imagePositionRight]: imagePosition === 'right',
            })}
        >
            <div className={style.imageContainer}>
                {picture?.url ? (
                    <Image
                        className={style.image}
                        alt={picture.alt}
                        src={picture.url}
                        unoptimized
                        fill
                        priority
                    />
                ) : null}
            </div>
            <div
                className={cx(style.contentContainer, {
                    ['backgroundWhite']: version === 'white',
                    ['backgroundYellow']: version === 'yellow',
                    ['backgroundGreen']: version === 'green',
                })}
            >
                {subtitle && !hasTwoButtons ? (
                    <Typography
                        as="h3"
                        scale="primary-m-extra-bold"
                        className={cx({
                            ['textYellow']: version === 'green',
                        })}
                    >
                        {subtitle.toUpperCase()}
                    </Typography>
                ) : null}
                <div className={hasTwoButtons ? style.contentTwoButtons : style.content}>
                    {title ? (
                        <div
                            className={
                                hasTwoButtons ? style.titleWrapperTwoButtons : style.titleWrapper
                            }
                        >
                            <Typography as="h2" scale="secondary-m-bold">
                                {title}
                            </Typography>
                            <BrandHandMarkTwoLinesVariant2
                                color={
                                    isLightBackground
                                        ? 'var(--color-primary-main)'
                                        : 'var(--color-secondary-main)'
                                }
                            />
                        </div>
                    ) : null}

                    <div
                        className={hasTwoButtons ? style.textWrapperTwoButtons : style.textWrapper}
                    >
                        <EvaneosPrismicRichText field={text} />
                        {ctaLabel && ctaLink?.url ? (
                            <div className={hasTwoButtons ? style.buttonsWrapper : undefined}>
                                <Button
                                    priority={isButtonPrimary ? 'primary' : 'secondary'}
                                    href={ctaLink?.url}
                                    target={target || undefined}
                                    onClick={() => {
                                        trackEvent((ampli, defaultProperties) => {
                                            ampli.comboImageTextBlockClicked({
                                                ...defaultProperties,
                                                card_number: 1,
                                                block_index: sliceIndex ? sliceIndex + 1 : 0,
                                                block_title: title ?? undefined,
                                            });
                                        });
                                    }}
                                >
                                    {ctaLabel}
                                </Button>

                                {hasTwoButtons ? (
                                    <Button
                                        priority="secondary"
                                        href={secondCtaLink?.url}
                                        target={secondCtaTarget || undefined}
                                        reversed={version === 'green' ? true : false}
                                        onClick={() => {
                                            trackEvent((ampli, defaultProperties) => {
                                                ampli.comboImageTextBlockClicked({
                                                    ...defaultProperties,
                                                    card_number: 2,
                                                    block_index: sliceIndex ? sliceIndex + 1 : 0,
                                                    block_title: title ?? undefined,
                                                });
                                            });
                                        }}
                                    >
                                        {secondCtaLabel}
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
